/**
 * Chapter order (decision 62: fractional positions, ties broken by record ID, a chapter with no valid position at the
 * end; part 08 log, principles 3 and 4, table O). A position is a fraction in base 62 written as its digits after the
 * point, so code-unit order is numeric order and there is always room between two different ones. Pure.
 */

/** The digits, in code-unit order. */
const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const BASE = DIGITS.length;

/** The longest valid position (O3). Placement widens its window rather than write a longer one (O7). */
export const MAX_POSITION_LENGTH = 128;

const POSITION = /^[0-9A-Za-z]*[1-9A-Za-z]$/u;

/** Whether `value` is a valid position (O3): 1 to {@link MAX_POSITION_LENGTH} base-62 digits, the last not `0`. */
export function isPosition(value: unknown): value is string {
  return typeof value === 'string' && value.length <= MAX_POSITION_LENGTH && POSITION.test(value);
}

/** The value of the digit at `index` of `digits`, 0 past its end (where `charAt` gives `''`, found at 0). */
function digitAt(digits: string, index: number): number {
  return DIGITS.indexOf(digits.charAt(index));
}

/** Digits greater than `digits` and below 1, ending in a digit other than `0`. */
function above(digits: string): string {
  let index = 0;
  while (digitAt(digits, index) === BASE - 1) index++;
  return digits.slice(0, index) + DIGITS.charAt(Math.floor((digitAt(digits, index) + BASE) / 2));
}

/**
 * A position strictly between `lo` and `hi`; null stands for 0 below and 1 above. Throws a RangeError unless both are
 * valid (or null) and `lo < hi`. At most one digit longer than the longer of the two.
 */
export function between(lo: string | null, hi: string | null): string {
  const low = lo ?? '';
  if ((lo !== null && !isPosition(lo)) || (hi !== null && !(isPosition(hi) && low < hi))) {
    throw new RangeError(`no position between ${String(lo)} and ${String(hi)}`);
  }
  let prefix = '';
  for (let index = 0; ; index++) {
    const a = digitAt(low, index);
    const b = hi === null ? BASE : digitAt(hi, index);
    if (b - a > 1) return prefix + DIGITS.charAt(Math.floor((a + b) / 2));
    prefix += DIGITS.charAt(a);
    // One apart: anything above the rest of `lo` under this prefix is below `hi`.
    if (b > a) return prefix + above(low.slice(index + 1));
  }
}

/**
 * `items` paired with positions in increasing order strictly between `lo` and `hi` (null: unbounded), each as short as
 * bisection makes it, or null when one would pass {@link MAX_POSITION_LENGTH}.
 */
export function spreadOver<T>(lo: string | null, hi: string | null, items: readonly T[]): [T, string][] | null {
  const out: [T, string][] = [];
  const fill = (low: string | null, high: string | null, part: readonly T[]): boolean => {
    const half = part.length >> 1;
    const [middle, ...right] = part.slice(half);
    if (middle === undefined) return true;
    const mid = between(low, high);
    if (mid.length > MAX_POSITION_LENGTH) return false;
    if (!fill(low, mid, part.slice(0, half))) return false;
    out.push([middle, mid]);
    return fill(mid, high, right);
  };
  return fill(lo, hi, items) ? out : null;
}

/** `count` positions in increasing order strictly between `lo` and `hi`, as {@link spreadOver} gives them, or null. */
export function spread(lo: string | null, hi: string | null, count: number): string[] | null {
  return spreadOver(lo, hi, Array.from({ length: count }, () => null))?.map(([, position]) => position) ?? null;
}

/** What order needs of a chapter entry. */
export interface Positioned {
  readonly id: string;
  readonly position: string;
}

/** The order of two entries (O1–O3): valid positions first, by position then ID; invalid ones by ID. */
export function compareChapters(a: Positioned, b: Positioned): number {
  const validA = isPosition(a.position);
  const validB = isPosition(b.position);
  if (validA !== validB) return validA ? -1 : 1;
  return (validA ? codeUnitOrder(a.position, b.position) : 0) || codeUnitOrder(a.id, b.id);
}

/** -1, 0 or 1 as `a` comes before, with or after `b` in code-unit order. */
function codeUnitOrder(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** `entries` in book order (principle 3), as a new array. */
export function orderChapters<T extends Positioned>(entries: readonly T[]): T[] {
  return [...entries].sort(compareChapters);
}

/** Where {@link place} puts new items (with their positions, in order), and the neighbours it had to move. */
export interface Placement<T, N> {
  readonly placed: readonly (readonly [N, string])[];
  readonly moved: readonly (readonly [T, string])[];
}

/** One place in a placement window: an entry already there, or a new item. */
type Target<T, N> = { readonly kind: 'old'; readonly entry: T } | { readonly kind: 'new'; readonly item: N };

/**
 * Positions for new `items` at `slot` of `ordered` (entries in book order; 0 is the start, `ordered.length` the end),
 * so that they sit there in order (principle 4, O4–O7). When the gap has no room, the fewest neighbours get fresh
 * positions: those tied with the one before the slot, and the invalid ones before it; the window widens only when the
 * length cap needs it. Throws a RangeError for a slot outside the list.
 */
export function place<T extends Positioned, N>(ordered: readonly T[], slot: number, items: readonly N[]): Placement<T, N> {
  const n = ordered.length;
  if (!Number.isInteger(slot) || slot < 0 || slot > n) throw new RangeError(`can't place at ${String(slot)} of ${String(n)}`);
  // Null for an invalid position, and outside the list: before the start and past the end are unbounded.
  const positionAt = (index: number): string | null => {
    const entry = ordered[index];
    return entry !== undefined && isPosition(entry.position) ? entry.position : null;
  };
  let firstInvalid = 0;
  while (positionAt(firstInvalid) !== null) firstInvalid++;
  // O6: the invalid ones before the slot need positions for the new ones to follow them.
  let start = Math.min(slot, firstInvalid);
  let end = slot;
  const old = (entry: T): Target<T, N> => ({ kind: 'old', entry });
  for (;;) {
    const low = positionAt(start - 1);
    // O5: every following neighbour not above `low` moves; the invalid ones after the slot stay at the end.
    for (;;) {
      const next = positionAt(end);
      if (next === null || low === null || next > low) break;
      end++;
    }
    const high = positionAt(end);
    const targets: Target<T, N>[] = [...ordered.slice(start, slot).map(old), ...items.map((item): Target<T, N> => ({ kind: 'new', item })), ...ordered.slice(slot, end).map(old)];
    const fresh = spreadOver(low, high, targets);
    if (fresh !== null) {
      return {
        placed: fresh.flatMap(([target, position]): [N, string][] => (target.kind === 'new' ? [[target.item, position]] : [])),
        moved: fresh.flatMap(([target, position]): [T, string][] => (target.kind === 'old' && target.entry.position !== position ? [[target.entry, position]] : [])),
      };
    }
    // O7: widen, doubling, towards whichever side has room left.
    const width = end - start + 1;
    if (end < n) end = Math.min(n, end + width);
    else start = Math.max(0, start - width);
  }
}
